import { ExpandMore } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography
} from '@mui/material';
import { useNotificationContext } from 'core/providers/NotificationContextProvider';
import { hasActivePeriod } from 'core/services/budget.service';
import { budgetsSelector } from 'core/store/selectors/budgets.selector';
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { IBudget } from '../../../common/entities';
import { useAppDispatch, useAppSelector } from '../../../core/hooks/hooks';
import { useContextMenu } from '../../../core/hooks/useContextMenu';
import { IEntityContext } from '../../../core/hooks/useEntityContext';
import { deleteBudget } from '../../../core/store/actions/budgets.actions';
import { BudgetListItem } from './BudgetListItem';

export interface IBudgetList {
  context: IEntityContext<string>;
  openEditForm: () => void;
  budgets: IBudget[];
}

const BudgetsPanel = ({
  expandable = false,
  children,
  title
}: PropsWithChildren<{ title: string; expandable?: boolean }>) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const onChange = () => setExpanded(!expanded);
  return expandable ? (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{ borderBottom: 'none', '&:before': { content: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          justifyContent: 'flex-start',
          flexDirection: 'row',
          '& .MuiAccordionSummary-content': {
            mt: 0,
            flexGrow: 0,
            '& .MuiTypography-root': { fontSize: '.8rem', fontWeight: 500 }
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            mt: 0,
            ml: 1,
            flexGrow: 0
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            mt: 1
          }
        }}
      >
        <Typography>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  ) : (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

const BudgetList: FC<IBudgetList> = ({ context, openEditForm, budgets }) => {
  const { showNotification } = useNotificationContext();
  const dispatch = useAppDispatch();
  const contextMenu = useContextMenu(context);
  const { lastAction, error } = useAppSelector(budgetsSelector);

  const activeBudgets = useMemo(
    () =>
      budgets.filter(
        b =>
          !b.accumulation && !b.isDeleted && (!b.single || hasActivePeriod(b))
      ),
    [budgets]
  );
  const inactiveBudgets = useMemo(
    () =>
      budgets.filter(
        b => !b.accumulation && !b.isDeleted && b.single && !hasActivePeriod(b)
      ),
    [budgets]
  );
  const activeAccumulations = useMemo(
    () => budgets.filter(b => b.accumulation && !b.isDeleted && !b.closed),
    [budgets]
  );
  const inactiveAccumulations = useMemo(
    () => budgets.filter(b => b.accumulation && !b.isDeleted && b.closed),
    [budgets]
  );

  useEffect(() => {
    switch (lastAction) {
      case deleteBudget.fulfilled.type:
        showNotification({
          message: `Budget has been deleted`
        });
        break;
      case deleteBudget.rejected.type:
        showNotification({ isError: true, message: `Error: ${error}` });
        break;
    }
  }, [lastAction]);

  const onOpenEditForm = () => {
    contextMenu.hideMenu();
    openEditForm();
  };

  const renderItemMenu = useCallback(() => {
    const handleDeleteBudget = () => {
      context.current && dispatch(deleteBudget(context.current));
      contextMenu.closeMenu();
    };

    return (
      <Menu
        open={contextMenu.menuOpen}
        anchorEl={contextMenu.anchorEl}
        onClose={contextMenu.closeMenu}
      >
        <MenuItem onClick={onOpenEditForm}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteBudget}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    );
  }, [context]);

  return (
    <Paper sx={{ width: '100%', p: 3, mb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <BudgetsPanel title="Active budgets">
          <List>
            {activeBudgets.map(b => (
              <BudgetListItem budget={b} onMenuClick={contextMenu.menuClick} />
            ))}
          </List>
        </BudgetsPanel>
        <BudgetsPanel title="Inactive budgets" expandable>
          <List>
            {inactiveBudgets.map(b => (
              <BudgetListItem budget={b} onMenuClick={contextMenu.menuClick} />
            ))}
          </List>
        </BudgetsPanel>
      </Box>
      <BudgetsPanel title="Accumulations">
        <List>
          {activeAccumulations.map(b => (
            <BudgetListItem budget={b} onMenuClick={contextMenu.menuClick} />
          ))}
        </List>
      </BudgetsPanel>
      {inactiveAccumulations.length > 0 && (
        <BudgetsPanel title="Closed accumulations" expandable>
          <List>
            {inactiveAccumulations.map(b => (
              <BudgetListItem budget={b} onMenuClick={contextMenu.menuClick} />
            ))}
          </List>
        </BudgetsPanel>
      )}

      {renderItemMenu()}
    </Paper>
  );
};

export default BudgetList;
